"use client";

import Link from "next/link";
import { useFormState } from "react-dom";
import { usePathname } from "next/navigation";
import { storyblokEditable } from "@storyblok/react/rsc";
import { track } from "@vercel/analytics";

import { GridContainer } from "@/components/layout/GridContainer";
import {
  MaskShape,
  type MaskShapeVariant,
} from "@/components/illustrations/MaskShape";
import { Input } from "@/components/ui/input";
import { RichTextField } from "@/components/typography/RichTextField";
import { SubmitButton } from "@/components/forms/SubmitButton";
import { subscribe } from "@/app/email/newsletter/actions";

type NewsletterCtaStoryblok = {
  title?: string;
  text?: string;
  button_label?: string;
  shape?: MaskShapeVariant;
  signup_topic?: string;
  _uid: string;
  component: "newsletter_cta";
  [k: string]: any;
};

type NewsletterCtaProps = {
  blok: NewsletterCtaStoryblok;
};

const NewsletterCta = ({ blok }: NewsletterCtaProps) => {
  const [state, formAction] = useFormState(subscribe, {});
  const pathname = usePathname();
  const shape = (blok.shape || "two_vertical_uneven") as MaskShapeVariant;

  return (
    <GridContainer {...storyblokEditable(blok)} className="md:px-8 lg:!px-0">
      <div className="col-span-12 lg:col-span-10 lg:col-start-2 py-4">
        <div className="relative bg-sky-50 rounded-3xl p-6 lg:p-10 overflow-hidden">
          {/* Shape background — md+ only, right-aligned */}
          <div className="hidden md:block absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 h-full w-1/2 opacity-20">
              <MaskShape
                variant={shape}
                className="w-full h-full"
                style={{ color: "white" }}
                preserveAspectRatio="none"
              />
            </div>
          </div>

          {/* Content — full width on mobile, left half on desktop */}
          <div className="relative z-[1] flex flex-col gap-6 items-center text-center md:items-start md:text-left md:max-w-md">
            {(blok.title || blok.text) && (
              <div className="flex flex-col gap-2">
                {blok.title && (
                  <h2 className="text-headline-xl text-black">{blok.title}</h2>
                )}
                {blok.text && (
                  <RichTextField value={blok.text} className="text-copy text-black" />
                )}
              </div>
            )}

            {/* Form */}
            <form action={formAction} className="w-full flex flex-col gap-1">
              <input type="hidden" name="topic" value={blok.signup_topic || "newsletter"} />
              <input type="hidden" name="source" value={pathname} />
              <Input
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Deine E-Mail"
                className="w-full !h-[46px] bg-transparent rounded-full px-5 border border-white !text-lg text-black placeholder:!text-lg placeholder:text-black"
                required
              />
              {state?.error && (
                <p className="text-copy-sm text-red-500">{state.error}</p>
              )}
              <SubmitButton
                variant="sun"
                size="sm"
                className="w-full h-[46px]"
                onClick={() => {
                  track("Newsletter submit");
                }}
              >
                {blok.button_label || "Anmelden"}
              </SubmitButton>

              <p className="text-copy-sm text-center md:text-left mt-1 mx-auto md:mx-0 max-w-[80%]">
                Mit Absenden des Formulars stimme ich den{" "}
                <Link href="/datenschutz" className="underline">
                  Datenschutzbestimmungen
                </Link>{" "}
                zu
              </p>

              <p aria-live="polite" className="sr-only">
                {state?.error}
              </p>
            </form>
          </div>
        </div>
      </div>
    </GridContainer>
  );
};

export default NewsletterCta;
